import { useMutation } from "@tanstack/react-query";
import { ArrowRight, Eye, EyeOff, Lock, User } from "lucide-react";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { AmLogo } from "@/components/AmLogo";
import { api, UnauthorizedError } from "@/lib/api";

export function Login(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const login = useMutation({
    mutationFn: () => api.login(username.trim(), password, remember),
    onSuccess: () => {
      const next = params.get("next");
      navigate(next && next.startsWith("/") ? next : "/", { replace: true });
    },
    onError: (err: unknown) => {
      setError(
        err instanceof UnauthorizedError
          ? "Credenziali non valide. Riprova."
          : err instanceof Error
            ? err.message
            : "Errore inatteso",
      );
    },
  });

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-paper px-4 py-10 text-ink">
      <div className="w-full max-w-[400px]">
        <div className="mb-7 flex flex-col items-center gap-3 text-center">
          <AmLogo size={48} />
          <div>
            <h1 className="page-title">Accedi ad AM</h1>
            <p className="mt-1.5 text-data text-ink-3">
              Prezzi e margini dei marketplace. Usa le tue credenziali BS COM.
            </p>
          </div>
        </div>
        <form
          className="rounded-[var(--radius-lg)] border border-line bg-card px-6 py-6 shadow-sm"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            login.mutate();
          }}
        >
          <div>
            <label htmlFor="username" className="mb-1.5 block text-label font-semibold text-ink-2">
              Utente
            </label>
            <div className="relative flex items-center">
              <User
                className="absolute left-3.5 h-4 w-4 text-ink-3"
                strokeWidth={1.6}
                aria-hidden="true"
              />
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-11 w-full rounded-[var(--radius)] border border-line-2 bg-card pl-10 pr-3 text-md outline-none transition-colors focus:border-key"
                autoFocus
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="password" className="mb-1.5 block text-label font-semibold text-ink-2">
              Password
            </label>
            <div className="relative flex items-center">
              <Lock
                className="absolute left-3.5 h-4 w-4 text-ink-3"
                strokeWidth={1.6}
                aria-hidden="true"
              />
              <input
                id="password"
                name="password"
                type={showPw ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-11 w-full rounded-[var(--radius)] border border-line-2 bg-card pl-10 pr-10 text-md outline-none transition-colors focus:border-key"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Nascondi password" : "Mostra password"}
                aria-pressed={showPw}
                className="absolute right-2 grid h-7 w-7 place-items-center rounded-md text-ink-3 hover:bg-paper-2"
              >
                {showPw ? (
                  <EyeOff className="h-4 w-4" strokeWidth={1.6} />
                ) : (
                  <Eye className="h-4 w-4" strokeWidth={1.6} />
                )}
              </button>
            </div>
          </div>

          <label className="mt-3.5 flex cursor-pointer items-center gap-2 text-data text-ink-2">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="h-4 w-4 rounded border-line-2 accent-[hsl(var(--key))]"
            />
            Resta connesso
          </label>

          {error && (
            <div
              role="alert"
              className="mt-4 flex items-center gap-2 rounded-[var(--radius)] border border-destructive/35 bg-destructive/5 px-3 py-2.5 text-data text-destructive"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={login.isPending || !username || !password}
            className="btn btn-accent mt-6 h-11 w-full text-md"
          >
            {login.isPending ? "Accesso…" : "Accedi"}
            {!login.isPending && <ArrowRight className="h-[15px] w-[15px]" strokeWidth={1.8} />}
          </button>
        </form>
        <p className="mt-5 text-center text-label text-ink-3">BS COM · am.bscom.it</p>
      </div>
    </main>
  );
}
